import * as React from 'react'

// A preference and not the state of a page, so it outlives a reload. Why it lives here and
// why the key is not a parameter: packages/ui/docs/nessuna-stringa-qui-dentro.md.

export const RAIL_STORAGE_KEY = 'volee-rail-collapsed'

/** The guard wraps the CALL and not the lookup: in Safari's private mode `setItem` is what
 *  throws, so `@volee/auth`'s `storage(): Storage | null` shape would not catch it. */
function safely<T>(read: () => T, whenRefused: T): T {
  try {
    return read()
  } catch {
    return whenRefused
  }
}

export interface CollapsedRail {
  readonly collapsed: boolean
  readonly setCollapsed: (collapsed: boolean) => void
}

/** The two values `AppShell` asks for; the key is not a parameter, the two apps are two origins.
 *  In a `theater`, narrow and not remembered: packages/ui/docs/teatro-video-a-tutto-schermo.md § «La barra laterale si chiude da sola» */
export function useCollapsedRail(theater = false): CollapsedRail {
  // Lazy, or every render reads storage; and read on mount rather than at module load, so a
  // test can seed the value before rendering.
  const [collapsed, setState] = React.useState(() =>
    safely(() => globalThis.localStorage?.getItem(RAIL_STORAGE_KEY) === 'true', false),
  )
  const [inTheater, setInTheater] = React.useState<boolean | null>(null)
  if (!theater && inTheater !== null) setInTheater(null)

  const setCollapsed = React.useCallback(
    (next: boolean) => {
      if (theater) {
        setInTheater(next)
        return
      }
      setState(next)
      safely(() => globalThis.localStorage?.setItem(RAIL_STORAGE_KEY, String(next)), undefined)
    },
    [theater],
  )

  return { collapsed: theater ? (inTheater ?? true) : collapsed, setCollapsed }
}
